import { matchProviderFromHref } from './provider-registry';
import { sha256Hex, stableText } from './hash';
import type { CanonicalIntent, ElementEvidence, ProviderCategory, ProviderMatch } from './types';

export type DetectionResult = {
  canonical_intent: CanonicalIntent;
  provider: string;
  provider_category: ProviderCategory | 'unknown';
  target: string | null;
  target_hash: string | null;
  confidence: number;
  matched_rule_id: string;
  evidence: ElementEvidence;
};

export function actionableElement(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  return target.closest('a,button,[role="button"],input[type="submit"],input[type="button"]');
}

export async function evidenceForElement(element: Element): Promise<ElementEvidence> {
  const anchor = element.closest('a[href]');
  const href = anchor instanceof HTMLAnchorElement ? anchor.href : attr(element, 'href');
  const text = stableText(element.textContent ?? '');
  return {
    tag: element.tagName.toLowerCase(),
    href,
    text,
    aria_label: attr(element, 'aria-label'),
    title: attr(element, 'title'),
    role: attr(element, 'role'),
    dom_path_hash: await sha256Hex(domPath(element)),
  };
}

export async function detectIntent(element: Element): Promise<DetectionResult | null> {
  const evidence = await evidenceForElement(element);
  const text = [evidence.text, evidence.aria_label, evidence.title].filter(Boolean).join(' ').toLowerCase();
  const href = evidence.href;

  if (href?.startsWith('tel:')) return result('call', 'self', 'self', href, 0.95, 'href:tel', evidence);
  if (href?.startsWith('mailto:')) return result('email', 'self', 'self', href, 0.9, 'href:mailto', evidence);

  const provider = matchProviderFromHref(href);
  if (provider) return result(intentFromProvider(provider), provider.id, provider.category, href, provider.confidence, `provider_host:${provider.id}`, evidence);

  if (/\b(order|order online|order now|zamów|zamow|dostawa|delivery|takeout|carryout)\b/i.test(text)) return result('order', 'unknown', 'unknown', href, 0.55, 'text:order', evidence);
  if (/\b(reserve|reservation|book a table|zarezerwuj|rezerwacja|rezerwuj stolik)\b/i.test(text)) return result('reservation', 'unknown', 'unknown', href, 0.55, 'text:reservation', evidence);
  if (/\b(map|maps|directions|visit us|find us|jak dojechać|jak dojechac|dojazd|mapa|lokalizacje|locations)\b/i.test(text)) return result('directions', 'unknown', 'unknown', href, 0.55, 'text:directions', evidence);
  if ((href && /menu|\.pdf/i.test(href)) || /\b(menu|menus|zobacz menu|jadłospis|jadlospis|breakfast|lunch|dinner plates|beverages)\b/i.test(text)) return result('menu', 'self', 'self', href, 0.7, 'text_or_href:menu', evidence);
  if (/\b(catering|events|eventy|imprezy|corporate|party|book an event)\b/i.test(text)) return result('catering', 'self', 'self', href, 0.7, 'text:catering', evidence);
  if (/\b(promo|promocj|coupon|kupon|deal|deals|discount|rabat|save|off|kod rabatowy)\b/i.test(text)) return result('promo', 'self', 'self', href, 0.65, 'text:promo', evidence);
  if (/\b(loyalty|rewards|reward|join|sign up|odbierz kod|qr)\b/i.test(text)) return result('loyalty_qr', 'self', 'self', href, 0.7, 'text:loyalty', evidence);

  return null;
}

function result(
  canonicalIntent: CanonicalIntent,
  provider: string,
  providerCategory: ProviderCategory | 'unknown',
  target: string | null,
  confidence: number,
  matchedRuleId: string,
  evidence: ElementEvidence,
): DetectionResult {
  return { canonical_intent: canonicalIntent, provider, provider_category: providerCategory, target, target_hash: null, confidence, matched_rule_id: matchedRuleId, evidence };
}

function intentFromProvider(provider: ProviderMatch): CanonicalIntent {
  switch (provider.category) {
    case 'reservation':
      return 'reservation';
    case 'order_delivery':
    case 'order_direct':
      return 'order';
    case 'catering':
      return 'catering';
    case 'directions':
      return 'directions';
    case 'social':
      return 'social';
    case 'review':
      return 'review';
    case 'self':
      return 'other';
  }
}

function attr(element: Element, name: string): string | null {
  const value = element.getAttribute(name);
  return value && value.trim() ? value.trim() : null;
}

function domPath(element: Element): string {
  const parts: string[] = [];
  let current: Element | null = element;
  while (current && parts.length < 8) {
    const id = current.id ? `#${current.id}` : '';
    const classes = Array.from(current.classList).slice(0, 3).join('.');
    parts.push(`${current.tagName.toLowerCase()}${id}${classes ? `.${classes}` : ''}`);
    current = current.parentElement;
  }
  return parts.join('>');
}
